/* 365nails homepage experience release: 365nails-homepage-brand-ui-v15 */

const homepageHeroAutoplayDelay = 4000;

const setDatasetValue = (element, key, value) => {
  if (element && element.dataset[key] !== value) {
    element.dataset[key] = value;
  }
};

const setTextValue = (element, value) => {
  if (element && element.textContent.trim() !== value) {
    element.textContent = value;
  }
};

const setAttributeValue = (element, name, value) => {
  if (element && element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
};

const isEnglishHomepage = /^en(?:-|$)/i.test(document.documentElement?.lang || "en");

const homepageHeroNativeSlideCount = 4;

const homepageHeroStyleRoles = [
  "ready-wholesale",
  "brand-ready",
  "dropshipping",
  "live-selection",
];

const getNativeHomepageHeroSlides = (layer) => Array.from(
  layer?.querySelectorAll?.(".swiper-slide") || [],
).filter((slide) => !slide.classList?.contains("swiper-slide-duplicate"));

const decorateNativeHomepageHeroSlides = (mediaSlides, contentSlides) => {
  if (
    mediaSlides.length !== homepageHeroNativeSlideCount
    || contentSlides.length !== homepageHeroNativeSlideCount
  ) return false;

  const roles = contentSlides.map((slide, index) => {
    const role = homepageHeroStyleRoles[index];
    const mediaSlide = mediaSlides[index];
    setDatasetValue(mediaSlide, "mediaRole", role);
    setDatasetValue(mediaSlide, "mediaFit", "cover");
    setDatasetValue(slide, "contentRole", role);
    return role;
  });

  return roles.length === homepageHeroNativeSlideCount
    && roles.every(Boolean)
    && new Set(roles).size === homepageHeroNativeSlideCount;
};

const enhanceHomepageHero = () => {
  const hero = document.querySelector(".image-with-text-slider image-with-text-slider");
  if (!hero || hero.dataset.brandV2 === "four-focus") return;

  const legacyMedia = hero.querySelector(".image-slider__media");
  const content = hero.querySelector(".image-slider__content");
  const mediaSlides = getNativeHomepageHeroSlides(legacyMedia);
  const contentSlides = getNativeHomepageHeroSlides(content);

  if (!decorateNativeHomepageHeroSlides(mediaSlides, contentSlides)) {
    setDatasetValue(hero, "brandV2NativeState", "requires-four-native-blocks");
    return;
  }

  setDatasetValue(legacyMedia, "brandV2Layer", "media");
  setDatasetValue(content, "brandV2Layer", "content");
  const contentSlider = content?.querySelector(".swiper");
  const prefersReducedMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")
    ?.matches === true;
  const themeEditorActive = globalThis.Shopify?.designMode === true;

  if (contentSlider && (prefersReducedMotion || themeEditorActive)) {
    contentSlider.swiper?.autoplay?.stop?.();
    setDatasetValue(
      hero,
      "brandV2Autoplay",
      themeEditorActive ? "theme-editor-paused" : "reduced-motion",
    );
  } else if (contentSlider) {
    // One slider owns the autoplay clock; the native component synchronizes its media peer.
    setDatasetValue(contentSlider, "autoplay", String(homepageHeroAutoplayDelay));
    if (contentSlider.swiper?.params?.autoplay) {
      contentSlider.swiper.params.autoplay.delay = homepageHeroAutoplayDelay;
      contentSlider.swiper.params.autoplay.disableOnInteraction = false;
      contentSlider.swiper.autoplay?.start?.();
    }
    setDatasetValue(hero, "brandV2Autoplay", `${homepageHeroAutoplayDelay}ms`);
  }
  setDatasetValue(hero, "brandV2", "four-focus");
  setDatasetValue(hero, "brandV2Media", "native-four-focus");
  if (isEnglishHomepage) {
    setAttributeValue(
      hero,
      "aria-label",
      "365nails wholesale, customization, dropshipping, and live product selection",
    );
  }

  if (!document.querySelector(".homepage-v2-trust")) {
    const trust = document.createElement("aside");
    const eyebrow = document.createElement("p");
    const heading = document.createElement("h2");
    const list = document.createElement("ul");
    const facts = [
      ["Clear MOQ", "Buying rules stay visible before you commit."],
      ["Real product checks", "Review styles and quality details before ordering."],
      ["Custom packaging", "Connect nails, cards, cases, and branded packaging."],
      ["Global fulfillment", "Choose wholesale shipping or dropshipping support."],
    ];

    trust.className = "homepage-v2-trust page-width page-width--fixed";
    trust.setAttribute("aria-labelledby", "HomepageV2TrustHeading");
    setDatasetValue(trust, "brandV2", "compact-proof");
    eyebrow.className = "homepage-v2-trust__eyebrow visually-hidden";
    eyebrow.textContent = "Procurement confidence";
    heading.id = "HomepageV2TrustHeading";
    heading.className = "homepage-v2-trust__heading visually-hidden";
    heading.textContent = "Sourcing facts";
    list.className = "homepage-v2-trust__list";

    facts.forEach(([title, description]) => {
      const item = document.createElement("li");
      const itemTitle = document.createElement("strong");
      const itemDescription = document.createElement("span");
      itemTitle.textContent = title;
      itemDescription.className = "visually-hidden";
      itemDescription.textContent = description;
      item.append(itemTitle, itemDescription);
      list.append(item);
    });

    trust.append(eyebrow, heading, list);
    const heroSection = hero.closest?.(".shopify-section");
    heroSection?.insertAdjacentElement?.("afterend", trust);
  }
};

const bindHomepageHeroThemeEditor = () => {
  if (
    globalThis.Shopify?.designMode !== true
    || document.documentElement?.dataset?.homepageHeroEditorBound === "true"
  ) return;

  setDatasetValue(document.documentElement, "homepageHeroEditorBound", "true");
  document.addEventListener?.("shopify:section:load", enhanceHomepageHero);
  document.addEventListener?.("shopify:block:select", () => {
    const contentSlider = document.querySelector(
      ".image-with-text-slider image-with-text-slider .image-slider__content .swiper",
    );
    contentSlider?.swiper?.autoplay?.stop?.();
  });
};

const homepageEvidenceItems = [
  {
    mediaType: "video",
    imageUrl: "https://i.ytimg.com/vi/I0IcdWit9xI/maxresdefault.jpg",
    imageAlt: "365nails team member speaking inside the press-on nail workshop",
    imageWidth: "1280",
    imageHeight: "720",
    eyebrow: "Real workshop video",
    title: "Meet the workshop behind the order",
    description: "See the working environment where handmade press-on nail styles are prepared and reviewed.",
    action: "Watch the workshop video",
    href: "/pages/video-1v1-live-video-selection-service",
  },
  {
    mediaType: "video",
    imageUrl: "https://i.ytimg.com/vi/QCMcdqL3vaE/maxresdefault.jpg",
    imageAlt: "365nails team member holding press-on nail samples for a close product view",
    imageWidth: "1280",
    imageHeight: "720",
    eyebrow: "Product detail review",
    title: "Inspect styles before committing",
    description: "Use live video to look closer at nail shape, finish, decoration, and presentation before placing an order.",
    action: "Review video selection",
    href: "/pages/video-1v1-live-video-selection-service",
  },
  {
    mediaType: "image",
    mediaFit: "contain",
    imageUrl: "https://cdn.shopify.com/s/files/1/0918/6066/4603/files/f7daf6eeca8b9543e19b6ad010f6b5d0.png",
    imageAlt: "Finished private-label press-on nail box and branded nail set",
    imageWidth: "1000",
    imageHeight: "1000",
    eyebrow: "Finished packaging example",
    title: "Review a real branded packaging result",
    description: "See how a nail set, clear case, logo sticker, and outer box can be presented together.",
    action: "Explore packaging options",
    href: "/pages/private-label-packaging",
  },
];

const enhanceHomepageEvidence = () => {
  if (!isEnglishHomepage || document.querySelector(".homepage-v2-evidence")) return;

  const trust = document.querySelector(".homepage-v2-trust");
  if (!trust) return;

  const section = document.createElement("section");
  const header = document.createElement("div");
  const eyebrow = document.createElement("p");
  const heading = document.createElement("h2");
  const introduction = document.createElement("p");
  const grid = document.createElement("div");

  section.className = "homepage-v2-evidence page-width page-width--fixed";
  section.setAttribute("aria-labelledby", "HomepageV2EvidenceHeading");
  setDatasetValue(section, "brandV2", "real-evidence");
  header.className = "homepage-v2-evidence__header";
  eyebrow.className = "homepage-v2-evidence__eyebrow";
  eyebrow.textContent = "Real sourcing evidence";
  heading.id = "HomepageV2EvidenceHeading";
  heading.className = "homepage-v2-evidence__heading";
  heading.textContent = "Real sourcing, before you order";
  introduction.className = "homepage-v2-evidence__introduction";
  introduction.textContent = "Workshop, live selection, and finished packaging—shown with real examples.";
  grid.className = "homepage-v2-evidence__grid";

  homepageEvidenceItems.forEach((item) => {
    const card = document.createElement("article");
    const media = document.createElement("figure");
    const image = document.createElement("img");
    const mediaLabel = document.createElement("span");
    const body = document.createElement("div");
    const cardEyebrow = document.createElement("p");
    const cardTitle = document.createElement("h3");
    const cardDescription = document.createElement("p");
    const action = document.createElement("a");

    card.className = "homepage-v2-evidence__card";
    media.className = "homepage-v2-evidence__media";
    setDatasetValue(media, "mediaFit", item.mediaFit || "cover");
    image.src = item.imageUrl;
    image.alt = item.imageAlt;
    image.width = item.imageWidth;
    image.height = item.imageHeight;
    image.loading = "lazy";
    image.decoding = "async";
    image.setAttribute("sizes", "(min-width: 990px) 33vw, (min-width: 768px) 50vw, 100vw");
    mediaLabel.className = "homepage-v2-evidence__media-label";
    mediaLabel.textContent = item.mediaType === "video" ? "Real video" : "Real example";
    body.className = "homepage-v2-evidence__body";
    cardEyebrow.className = "homepage-v2-evidence__card-eyebrow";
    cardEyebrow.textContent = item.eyebrow;
    cardTitle.className = "homepage-v2-evidence__card-title";
    cardTitle.textContent = item.title;
    cardDescription.className = "homepage-v2-evidence__card-description";
    cardDescription.textContent = item.description;
    action.className = "homepage-v2-evidence__action";
    action.href = item.href;
    action.textContent = item.action;

    media.append(image, mediaLabel);
    body.append(cardEyebrow, cardTitle, cardDescription, action);
    card.append(media, body);
    grid.append(card);
  });

  header.append(eyebrow, heading, introduction);
  section.append(header, grid);
  const categorySection = document
    .querySelector(".collection-list-slider")
    ?.closest?.(".shopify-section");
  if (categorySection) {
    categorySection.insertAdjacentElement?.("afterend", section);
    setDatasetValue(section, "brandV2Placement", "after-product-categories");
  } else {
    trust.insertAdjacentElement?.("afterend", section);
    setDatasetValue(section, "brandV2Placement", "after-proof-strip");
  }
};

const enhanceBuyerPaths = () => {
  [
    document.querySelector(".buyer-next-step"),
    document.querySelector(".homepage-product-paths-wrapper"),
  ].forEach((section) => {
    if (!section || section.dataset.brandV2 === "hero-owned") return;
    section.hidden = true;
    setAttributeValue(section, "aria-hidden", "true");
    setDatasetValue(section, "brandV2", "hero-owned");
  });
};

const homepageProofFacts = [
  "Clear MOQ and wholesale pricing",
  "Custom packaging available",
  "Worldwide shipping support",
  "Quality checks before shipment",
  "Real product videos available",
  "Factory-direct sourcing",
];

const enhanceHomepageProofStrip = () => {
  const section = document.querySelector(".section--scrolling-promotion");
  if (!section || section.dataset.brandV2 === "bounded-proof") return;

  if (isEnglishHomepage) {
    section.querySelectorAll(".promotion__text").forEach((item, index) => {
      setTextValue(item, homepageProofFacts[index % homepageProofFacts.length]);
    });
  }

  setDatasetValue(section, "brandV2", "bounded-proof");
};

enhanceHomepageHero();
bindHomepageHeroThemeEditor();
enhanceHomepageEvidence();
enhanceBuyerPaths();
enhanceHomepageProofStrip();

const homepageProductCollectionConfig = [
  {
    handle: "new-arrivals",
    eyebrow: "Fresh for your assortment",
    heading: "New Arrivals",
    description: "Start with the newest press-on nail styles added for wholesale buyers.",
    action: "View New Arrivals",
    placement: "before",
  },
  {
    handle: "hot-products",
    eyebrow: "Best sellers for new buyers",
    heading: "First-order Best Sellers",
    description: "Start with proven styles that help wholesale buyers test demand with less guesswork.",
    action: "View Hot Products",
    placement: "source",
  },
  {
    handle: "clearance-sale",
    eyebrow: "Margin-focused deals",
    heading: "Flash Deals & Clearance Picks",
    description: "Use current clearance offers to test styles, fill bundles, and protect margin.",
    action: "View All Deals",
    placement: "after",
  },
];

const formatHomepagePrice = (value) => {
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
};

const updateHomepageProductCard = (card, product) => {
  const href = `/products/${product.handle}`;
  const imageUrl = product.images?.[0]?.src || product.image?.src || "";
  const price = formatHomepagePrice(product.variants?.[0]?.price);

  card.querySelectorAll("a[href]").forEach((link) => {
    setAttributeValue(link, "href", href);
    if (link.hasAttribute("aria-label")) {
      setAttributeValue(link, "aria-label", product.title);
    }
  });

  const image = card.querySelector(".product-card__image img");
  if (image && imageUrl) {
    image.src = imageUrl;
    image.alt = product.title;
    image.loading = "lazy";
    image.decoding = "async";
    image.removeAttribute("srcset");
    image.removeAttribute("is");
    image.removeAttribute("fetchpriority");
    image.closest(".media-wrapper")?.classList.remove("loading");
  }

  setTextValue(card.querySelector(".product-card__title .reversed-link__text"), product.title);
  const titleLink = card.querySelector(".product-card__title a");
  setAttributeValue(titleLink, "data-product-card-title-full", product.title);
  setAttributeValue(titleLink, "data-product-card-title-short", product.title);

  card.querySelector(".product-card__badge")?.remove();
  card.querySelector(".f-price__sale")?.remove();
  card.querySelector(".f-price")?.classList.remove("f-price--on-sale");
  const regularPrice = card.querySelector(".f-price__regular .f-price-item--regular");
  setTextValue(regularPrice, price ? `From ${price} / set` : "");
  card.querySelectorAll(
    ".product-card__list-actions, [data-product-quick-view], [data-quick-add]",
  ).forEach((item) => item.remove());
};

const buildHomepageProductCollection = (sourceWrapper, config, products) => {
  const clone = sourceWrapper.cloneNode(true);
  clone.removeAttribute("id");
  clone.querySelectorAll("[id], [data-id]").forEach((item) => {
    item.removeAttribute("id");
    item.removeAttribute("data-id");
  });
  clone.querySelectorAll("script").forEach((script) => script.remove());
  clone.querySelectorAll("motion-element").forEach((item) => {
    item.removeAttribute("data-motion");
    item.removeAttribute("data-motion-delay");
    item.removeAttribute("style");
  });
  clone.classList.add("homepage-product-collection");
  setDatasetValue(clone, "homepageCollection", config.handle);

  setTextValue(clone.querySelector(".rich-text__subheading"), config.eyebrow);
  setTextValue(
    clone.querySelector(".rich-text__heading motion-element")
      || clone.querySelector(".rich-text__heading"),
    config.heading,
  );
  setTextValue(clone.querySelector(".rich-text__text"), config.description);

  const action = clone.querySelector(".section__header--buttons a");
  setTextValue(action?.querySelector(".btn__text") || action, config.action);
  setAttributeValue(action, "href", `/collections/${config.handle}`);

  const cards = Array.from(clone.querySelectorAll(".product-card"));
  cards.forEach((card, index) => {
    const column = card.closest(".f-column");
    const product = products[index];
    if (!product) {
      column?.remove();
      return;
    }
    updateHomepageProductCard(card, product);
  });

  return clone;
};

const enhanceHomepageProductCollections = async () => {
  if (!isEnglishHomepage || !globalThis.fetch) return;
  if (document.querySelector("[data-homepage-collection='new-arrivals']")) return;

  const bestSellerSection = Array.from(document.querySelectorAll(".featured-collection"))
    .find((section) => section.textContent?.includes("First-order Best Sellers"));
  const bestSellerWrapper = bestSellerSection?.closest(".shopify-section");
  if (!bestSellerWrapper) return;

  try {
    const collectionResponses = await Promise.all(
      homepageProductCollectionConfig.map(async (config) => {
        const response = await fetch(`/collections/${config.handle}/products.json?limit=5`, {
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error(`Collection request failed: ${config.handle}`);
        const payload = await response.json();
        return { config, products: payload.products || [] };
      }),
    );
    if (collectionResponses.some(({ products }) => products.length === 0)) return;

    collectionResponses.forEach(({ config, products }) => {
      if (config.placement === "source") {
        Array.from(bestSellerWrapper.querySelectorAll(".product-card"))
          .forEach((card, index) => {
            if (products[index]) updateHomepageProductCard(card, products[index]);
          });
        setDatasetValue(bestSellerWrapper, "homepageCollection", config.handle);
        return;
      }

      const collection = buildHomepageProductCollection(bestSellerWrapper, config, products);
      if (config.placement === "before") {
        bestSellerWrapper.insertAdjacentElement("beforebegin", collection);
      } else {
        bestSellerWrapper.insertAdjacentElement("afterend", collection);
      }
    });

    const legacyDeals = document.querySelector(".countdown-with-multi")?.closest(".shopify-section");
    const legacyTrends = document.querySelector(".horizontal-products-list")?.closest(".shopify-section");
    [legacyDeals, legacyTrends].forEach((section) => {
      if (!section) return;
      section.hidden = true;
      section.setAttribute("aria-hidden", "true");
      setDatasetValue(section, "brandV2", "replaced-product-module");
    });
  } catch {
    // Preserve the native homepage modules if the collection endpoint is unavailable.
  }
};

const enhanceHomepageCustomProducts = async () => {
  if (!isEnglishHomepage || !globalThis.fetch) return;

  const section = Array.from(document.querySelectorAll(".featured-collection"))
    .find((item) => item.textContent?.includes("Custom & Brand-ready Picks"));
  if (!section || section.dataset.homepageCustomProducts === "product-only") return;

  const items = section.querySelector(".featured-collection__items");
  const promotionalColumn = Array.from(items?.children || [])
    .find((column) => column.querySelector?.(".card-media")
      && !column.querySelector?.(".product-card"));
  const sourceProductCard = items?.querySelector?.(".product-card");
  const sourceProductColumn = sourceProductCard?.closest?.(".f-column");
  if (!promotionalColumn || !sourceProductColumn) return;

  try {
    const response = await fetch("/collections/private-label/products.json?limit=5", {
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("Custom product collection request failed.");
    const payload = await response.json();
    const replacementProduct = payload.products?.[4];
    if (!replacementProduct) return;

    const productColumn = sourceProductColumn.cloneNode(true);
    productColumn.querySelectorAll("[id], [data-id]").forEach((item) => {
      item.removeAttribute("id");
      item.removeAttribute("data-id");
    });
    productColumn.querySelectorAll("script").forEach((script) => script.remove());
    productColumn.querySelectorAll("motion-element").forEach((item) => {
      item.removeAttribute("data-motion");
      item.removeAttribute("data-motion-delay");
      item.removeAttribute("style");
    });

    const productCard = productColumn.querySelector(".product-card");
    if (!productCard) return;
    updateHomepageProductCard(productCard, replacementProduct);
    promotionalColumn.replaceWith(productColumn);
    setDatasetValue(section, "homepageCustomProducts", "product-only");
  } catch {
    // Keep the merchant-configured section intact if the collection endpoint is unavailable.
  }
};

const homepageTopSeries = [
  {
    title: "3D Water Tube",
    href: "/collections/liquid-motion-press-ons",
    image: "//365nails.com/cdn/shop/collections/IMG_E4628.jpg?v=1776737877&width=900",
  },
  {
    title: "Cat Eye",
    href: "/collections/cat-eye",
    image: "//365nails.com/cdn/shop/collections/screenshot-20250730-085152.png?v=1753836751&width=900",
  },
  {
    title: "Crystal Luxury",
    href: "/collections/crystal-luxury",
    image: "//365nails.com/cdn/shop/collections/O1CN01DCmC5d22SYgO3eUB3__2219370087119-0-cib.jpg?v=1767273153&width=900",
  },
  {
    title: "Dragon Claw",
    href: "/collections/dragon-claw",
    image: "//365nails.com/cdn/shop/collections/7e60852bf732f78a3ea06878760081c4.jpg?v=1764325532&width=900",
  },
  {
    title: "Sculpted Flora",
    href: "/collections/sculpted-flora",
    image: "//365nails.com/cdn/shop/collections/c099e5267d3180141400c91b19b221b1.jpg?v=1767575408&width=900",
  },
  {
    title: "Femme Fatale",
    href: "/collections/femme-fatale",
    image: "//365nails.com/cdn/shop/collections/4b775fd3a3c68a4d1de5efc4d36e30ca.jpg?v=1771386469&width=900",
  },
  {
    title: "Dark Rebel",
    href: "/collections/dark-rebel",
    image: "//365nails.com/cdn/shop/collections/O1CN01B1wZGk1rlpWt19d1F__2215158925672-0-cib.jpg?v=1766826318&width=900",
  },
  {
    title: "Cartoon & Dopamine",
    href: "/collections/cartoon-dopamine",
    image: "//365nails.com/cdn/shop/collections/d6c53bbf6b079c56535debb8eb10dd33.jpg?v=1771295174&width=900",
  },
  {
    title: "Solid Color",
    href: "/collections/solid-color-1",
    image: "//365nails.com/cdn/shop/collections/jimeng-2025-11-25-3316.png?v=1764030522&width=900",
  },
  {
    title: "Rhinestone",
    href: "/collections/rhinestone",
    image: "//365nails.com/cdn/shop/files/O1CN01It3ueZ1CPwH3Yb59C__2213196780074-0-cib.jpg?v=1753689594&width=900",
  },
];

const withHomepageImageWidth = (url, width) => url.replace(
  /([?&])width=\d+/,
  `$1width=${width}`,
);

const enhanceHomepageTopSeries = () => {
  if (!isEnglishHomepage) return;
  const section = Array.from(document.querySelectorAll(".collection-list"))
    .find((item) => item.textContent?.includes("Series That Help Buyers Decide Faster"));
  if (!section || section.dataset.brandV2 === "top-series") return;

  const cards = Array.from(section.querySelectorAll("a.collection-card__wrapper")).slice(0, 10);
  if (cards.length < homepageTopSeries.length) return;

  cards.forEach((card, index) => {
    const series = homepageTopSeries[index];
    const image = card.querySelector("img");
    setAttributeValue(card, "href", series.href);
    setTextValue(card.querySelector(".collection-card__title span"), series.title);
    setAttributeValue(card.querySelector(".collection-card__btn"), "aria-label", series.title);
    if (image) {
      image.src = series.image;
      image.alt = series.title;
      image.setAttribute(
        "srcset",
        [360, 535, 750, 900]
          .map((width) => `${withHomepageImageWidth(series.image, width)} ${width}w`)
          .join(", "),
      );
    }
  });
  setDatasetValue(section, "brandV2", "top-series");
};

void enhanceHomepageProductCollections();
void enhanceHomepageCustomProducts();
enhanceHomepageTopSeries();

const popup = document.querySelector("subscription-popup.subscription-popup");

if (popup && !customElements.get("subscription-popup")) {
  setDatasetValue(popup, "triggerOpen", "scroll_down");
  setDatasetValue(popup, "repeatOpen", "every_week");
  setDatasetValue(popup, "brandUiRelease", "365nails-homepage-brand-ui-v15");
}

const dealCollection = document.querySelector(".countdown-with-multi");

if (dealCollection && !dealCollection.closest?.(".shopify-section")?.hidden) {
  const lead = dealCollection.querySelector(".countdown-with-multi__timer");
  const sourceLink = lead?.querySelector("a.card-media--link");
  const sourceEyebrow = lead?.querySelector(".rich-text__subheading")?.textContent.trim();
  const sourceHeading = lead?.querySelector(".card-media__heading")?.textContent.trim();
  const sourceDescription = lead?.querySelector(".card-media__description")?.textContent.trim();

  if (!document.querySelector(".homepage-deals-header")) {
    const header = document.createElement("div");
    const text = document.createElement("div");
    const eyebrow = document.createElement("p");
    const heading = document.createElement("h2");
    const description = document.createElement("p");
    const action = document.createElement("a");

    header.className = "homepage-deals-header";
    text.className = "homepage-deals-header__text";
    eyebrow.className = "homepage-deals-header__eyebrow";
    heading.className = "homepage-deals-header__heading";
    description.className = "homepage-deals-header__description";
    action.className = "homepage-deals-header__action";

    eyebrow.textContent = sourceEyebrow || "Margin-focused deals";
    heading.textContent = sourceHeading || "Flash Deals & Clearance Picks";
    description.textContent = sourceDescription || "Use clearance offers to test styles and protect margin.";
    action.textContent = "View All Deals";
    action.href = sourceLink?.getAttribute("href") || "/collections/clearance-sale";

    text.append(eyebrow, heading, description);
    header.append(text, action);
    dealCollection.parentNode?.insertBefore(header, dealCollection);
  }

  const leadHeading = lead?.querySelector(".card-media__heading");
  if (leadHeading?.textContent.trim() !== "Clearance & Mystery Boxes") {
    leadHeading.textContent = "Clearance & Mystery Boxes";
  }

  if (sourceLink?.getAttribute("aria-label") !== "Shop clearance and mystery boxes") {
    sourceLink?.setAttribute("aria-label", "Shop clearance and mystery boxes");
  }

  setDatasetValue(dealCollection, "brandUiLayout", "collection-row");
}

const nailShapeOptions = [
  ["square", "Square"],
  ["almond", "Almond"],
  ["coffin", "Coffin"],
  ["oval", "Oval"],
  ["stiletto", "Stiletto"],
  ["duck", "Duck"],
  ["round", "Round"],
  ["other", "Other shapes"],
];

document.querySelectorAll(".brand-logos__items > .f-column").forEach((item, index) => {
  const option = nailShapeOptions[index];
  const link = item.querySelector(".brand-logos__image");

  if (!option || !link) return;

  const [slug, label] = option;
  setDatasetValue(link, "brandShape", slug);
  setDatasetValue(link, "brandShapeLabel", label);

  const accessibleLabel = `Shop ${label} nail shapes`;
  if (link.getAttribute("aria-label") !== accessibleLabel) {
    link.setAttribute("aria-label", accessibleLabel);
  }
});
